import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { announcementService } from "@/features/announcements/services/feedService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { ViewersModal, type Viewer } from "@/features/announcements/components/ViewersModal";
import { CommentSection } from "@/features/announcements/components/CommentSection";
import { PollResultsModal } from "@/features/announcements/components/PollResultsModal";
import { Modal } from "@/shared/components/ui/Modal";
import { formatDate, formatDisplayName } from "@/shared/utils/formatters";
import type { Announcement } from "@/features/announcements/types/feed";

interface CompanyOption {
  value: string;
  label: string;
}

interface PosterOption {
  value: string;
  label: string;
}

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

type SortKey =
  | "title"
  | "company"
  | "created_by"
  | "date"
  | "comments"
  | "views";
type SortDir = "asc" | "desc";

const OFFICER_TYPES = ["officer", "admin"];

export const AnnouncementManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );
  const isSouserWithWrite = userData?.userType?.toLowerCase() === "souser" && !!userData?.announcements_write;
  const canManage = isOfficer || isSouserWithWrite;
  const canEdit = (item: Announcement) => isOfficer || (isSouserWithWrite &&
    String(item.created_by) === String(userData?.id) &&
    (!userData?.souser_scope || userData.souser_scope.writable_business_units.includes(item.business_unit ?? "")));

  // Redirect if no permission
  useEffect(() => {
    if (!canManage) {
      navigate("/", { replace: true });
    }
  }, [canManage, navigate]);

  // Data
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  // Sort state
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Filter state
  const isSouser = userData?.userType?.toLowerCase() === "souser";
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [batchFilter, setBatchFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState<string[]>(
    []
  );
  const [sendingOrgFilter, setSendingOrgFilter] = useState(
    isSouser && userData?.souser_sending_org ? userData.souser_sending_org : ""
  );
  const [showPubliclyFilter, setShowPubliclyFilter] = useState<
    "all" | "public" | "private"
  >("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  // Dropdown data
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [, setPosters] = useState<PosterOption[]>([]);

  // Company multi-select dropdown
  const [companySearch, setCompanySearch] = useState("");
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  // Country multi-select dropdown
  const [countrySearch, setCountrySearch] = useState("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // Sending org dropdown data (populated when exactly 1 country is selected)
  const [sendingOrgs, setSendingOrgs] = useState<{ value: string; label: string }[]>([]);

  // Viewers modal state
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [showViewers, setShowViewers] = useState(false);
  const [loadingViewersId, setLoadingViewersId] = useState<number | null>(null);

  // Comments modal state
  const [viewingCommentsId, setViewingCommentsId] = useState<number | null>(
    null,
  );
  const [viewingPoll, setViewingPoll] = useState<Announcement | null>(null);

  // Delete state
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");

  // Load dropdown data
  useEffect(() => {
    if (isSouser) return;
    announcementService
      .getCompaniesWithUsers()
      .then(setCompanies)
      .catch(console.error);
    announcementService
      .getPosters()
      .then((data: any[]) => {
        const normalized = data.map((p: any) => ({
          value: String(p.value ?? p.user_id ?? p.id ?? ""),
          label: String(p.label ?? p.name ?? p.author_name ?? ""),
        }));
        setPosters(normalized);
      })
      .catch(console.error);
  }, [isSouser]);

  // Close company dropdown on outside click
  useEffect(() => {
    if (!companyDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(e.target as Node)
      ) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [companyDropdownOpen]);

  // Close country dropdown on outside click
  useEffect(() => {
    if (!countryDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target as Node)
      ) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [countryDropdownOpen]);

  // Fetch sending orgs when exactly 1 country is selected; clear otherwise
  useEffect(() => {
    if (countryFilter.length === 1) {
      sendingOrgService
        .getDropdown(countryFilter[0])
        .then((data: any[]) =>
          setSendingOrgs(
            data.map((o: any) => ({ value: o.value ?? o.id, label: o.label ?? o.name })),
          ),
        )
        .catch(() => setSendingOrgs([]));
    } else {
      setSendingOrgs([]);
      setSendingOrgFilter("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(countryFilter)]);

  // Load announcements (unfiltered from API, we filter client-side)
  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const data = await announcementService.getAnnouncements(undefined, true);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load announcements:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  // Client-side filtering
  const filtered = announcements.filter((a) => {
    // Company filter
    if (companyFilter.length > 0) {
      const announcementCompanies = a.target_companies || a.company || [];
      const companyLabels = companies
        .filter((c) => companyFilter.includes(c.value))
        .map((c) => c.label.toLowerCase());
      const hasMatch = announcementCompanies.some((c: any) => {
        const label =
          typeof c === "string" ? c : c?.label || c?.company_name || "";
        return (
          companyFilter.includes(label) ||
          companyLabels.includes(label.toLowerCase())
        );
      });
      if (!hasMatch) return false;
    }

    // Batch filter
    if (batchFilter.trim()) {
      const batchLower = batchFilter.toLowerCase();
      if (!(a.batch_no || "").toLowerCase().includes(batchLower)) return false;
    }

    // Created by filter
    if (createdByFilter.trim()) {
      const createdLower = createdByFilter.toLowerCase();
      const authorName = (
        a.created_by_name ||
        a.author_name ||
        ""
      ).toLowerCase();
      if (!authorName.includes(createdLower)) return false;
    }

    // Country filter
    if (countryFilter.length > 0) {
      const announcementCountries = a.country || [];
      const hasMatch = announcementCountries.some((c: string) =>
        countryFilter.includes(c),
      );
      if (!hasMatch) return false;
    }

    // Sending org filter
    if (sendingOrgFilter.trim()) {
      const orgLower = sendingOrgFilter.toLowerCase();
      if (!(a.sending_org || "").toLowerCase().includes(orgLower)) return false;
    }

    // Show publicly filter (public = active true, private = active false)
    if (showPubliclyFilter === "public" && !a.active) return false;
    if (showPubliclyFilter === "private" && a.active) return false;

    // Date-based status filter
    if (statusFilter !== "all") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateFrom = a.date_from ? new Date(a.date_from) : null;
      const dateTo = a.date_to ? new Date(a.date_to) : null;
      if (dateFrom) dateFrom.setHours(0, 0, 0, 0);
      if (dateTo) dateTo.setHours(0, 0, 0, 0);
      const isDateActive = (!dateFrom || today >= dateFrom) && (!dateTo || today <= dateTo);
      if (statusFilter === "active" && !isDateActive) return false;
      if (statusFilter === "inactive" && isDateActive) return false;
    }

    return true;
  });

  // Sorting with useMemo
  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;

    copy.sort((a, b) => {
      switch (sortKey) {
        case "title":
          return dir * (a.title || "").localeCompare(b.title || "");
        case "company": {
          const aCompany = (a.target_companies || a.company || [])
            .map((c: any) => (typeof c === "string" ? c : c?.label || ""))
            .join(", ");
          const bCompany = (b.target_companies || b.company || [])
            .map((c: any) => (typeof c === "string" ? c : c?.label || ""))
            .join(", ");
          return dir * aCompany.localeCompare(bCompany);
        }
        case "created_by": {
          const aName = a.created_by_name || a.author_name || "";
          const bName = b.created_by_name || b.author_name || "";
          return dir * aName.localeCompare(bName);
        }
        case "date":
          return dir * (a.created_at || "").localeCompare(b.created_at || "");
        case "comments":
          return dir * ((a.comments_count ?? 0) - (b.comments_count ?? 0));
        case "views":
          return dir * ((a.view_count ?? 0) - (b.view_count ?? 0));
        default:
          return 0;
      }
    });

    return copy;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setPage(0); }, [companyFilter, countryFilter, batchFilter, createdByFilter, sendingOrgFilter, showPubliclyFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / 50));
  const pagedAnnouncements = sorted.slice(page * 50, (page + 1) * 50);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) {
      return (
        <svg
          className="w-3 h-3 ml-1 text-gray-300 inline"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortDir === "asc" ? (
      <svg
        className="w-3 h-3 ml-1 text-indigo-600 inline"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 ml-1 text-indigo-600 inline"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  // CSV export
  const handleExportCSV = () => {
    const headers = [
      "ID",
      "Title",
      "Created By",
      "Date",
      "Status",
      "Companies",
      "Batch",
      "Country",
      "Sending Org",
      "Comments",
      "Views",
    ];
    const rows = sorted.map((a) => {
      const companiesList = (a.target_companies || a.company || [])
        .map((c: any) =>
          typeof c === "string" ? c : c?.label || c?.company_name || "",
        )
        .join("; ");
      const countryList = (a.country || []).join("; ");
      return [
        String(a.row_id),
        `"${(a.title || "").replace(/"/g, '""')}"`,
        `"${(a.created_by_name || a.author_name || "").replace(/"/g, '""')}"`,
        a.created_at ? formatDate(a.created_at) : "",
        a.active ? "Active" : "Inactive",
        `"${companiesList.replace(/"/g, '""')}"`,
        a.batch_no || "",
        `"${countryList.replace(/"/g, '""')}"`,
        `"${(a.sending_org || "").replace(/"/g, '""')}"`,
        String(a.comments_count ?? 0),
        String(a.view_count ?? 0),
      ].join(",");
    });

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `announcements_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCompanyToggle = (companyId: string) => {
    setCompanyFilter((prev) =>
      prev.includes(companyId)
        ? prev.filter((c) => c !== companyId)
        : [...prev, companyId],
    );
  };

  const clearFilters = () => {
    setCompanyFilter([]);
    setBatchFilter("");
    setCreatedByFilter("");
    setCountryFilter([]);
    setSendingOrgFilter("");
    setShowPubliclyFilter("all");
    setStatusFilter("all");
  };

  const handleViewClick = async (announcementId: number) => {
    setLoadingViewersId(announcementId);
    try {
      const data = await announcementService.getViewers(announcementId);
      setViewers(data);
      setShowViewers(true);
    } catch (err) {
      console.error("Failed to load viewers:", err);
    } finally {
      setLoadingViewersId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await announcementService.deleteAnnouncement(deletingId);
      setAnnouncements((prev) => prev.filter((a) => a.row_id !== deletingId));
      setDeleteSuccess(
        t("feed.management.delete_success", "Announcement deleted successfully"),
      );
      setTimeout(() => setDeleteSuccess(""), 4000);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        t("feed.management.delete_error", "Failed to delete announcement");
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
      setDeletingId(null);
    }
  };

  if (!canManage) return null;

  return (
    <div>
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          {t("feed.management.title", "Announcement Management")}
        </h1>
        <Button onClick={() => navigate("/announcements/new")} className="w-full md:w-auto">
          {t("feed.create")}
        </Button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 mb-4 md:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {/* Company multi-select */}
          {!isSouser && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("feed.form.companies")}
              </label>

              {/* Selected chips */}
              {companyFilter.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {companyFilter.map((val) => {
                    const match = companies.find((c) => c.value === val);
                    return (
                      <span
                        key={val}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
                      >
                        {match?.label || val}
                        <button
                          type="button"
                          onClick={() => handleCompanyToggle(val)}
                          className="hover:text-indigo-900"
                        >
                          &times;
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="relative" ref={companyDropdownRef}>
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    if (!companyDropdownOpen) setCompanyDropdownOpen(true);
                  }}
                  onFocus={() => setCompanyDropdownOpen(true)}
                  placeholder={t("feed.form.search_company", "Search company...")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {companyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {companies
                      .filter((c) =>
                        c.label
                          ?.toLowerCase()
                          .includes(companySearch.toLowerCase()),
                      )
                      .map((c) => {
                        const isSelected = companyFilter.includes(c.value);
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => {
                              handleCompanyToggle(c.value);
                              setCompanySearch("");
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                              isSelected
                                ? "bg-indigo-50 text-indigo-700"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <span
                              className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${
                                isSelected
                                  ? "bg-indigo-600 border-indigo-600 text-white"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && "\u2713"}
                            </span>
                            {c.label}
                          </button>
                        );
                      })}
                    {companies.filter((c) =>
                      c.label
                        ?.toLowerCase()
                        .includes(companySearch.toLowerCase()),
                    ).length === 0 && (
                      <p className="px-3 py-2 text-xs text-gray-400 italic">
                        {t("common.no_results", "No results found")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch */}
          {!isSouser && (
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("feed.form.batch")}
              </label>
              <input
                type="text"
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                placeholder={t(
                  "feed.management.filter_batch",
                  "Filter by batch...",
                )}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* Country multi-select */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("feed.form.country", "Country")}
            </label>

            {/* Selected chips */}
            {countryFilter.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {countryFilter.map((code) => {
                  const opt = COUNTRY_OPTIONS.find((c) => c.code === code);
                  return (
                    <span
                      key={code}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        isSouser
                          ? "bg-gray-100 text-gray-600"
                          : "bg-indigo-100 text-indigo-700"
                      }`}
                    >
                      {opt ? t(`profile.${opt.key}`) : code}
                      {!isSouser && (
                        <button
                          type="button"
                          onClick={() =>
                            setCountryFilter((prev) => prev.filter((c) => c !== code))
                          }
                          className="hover:text-indigo-900"
                        >
                          &times;
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="relative" ref={countryDropdownRef}>
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => {
                  if (!isSouser) {
                    setCountrySearch(e.target.value);
                    if (!countryDropdownOpen) setCountryDropdownOpen(true);
                  }
                }}
                onFocus={() => {
                  if (!isSouser) setCountryDropdownOpen(true);
                }}
                disabled={isSouser}
                placeholder={t("feed.form.search_country", "Search country...")}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  isSouser
                    ? "bg-gray-50 text-gray-400 cursor-not-allowed disabled:border-gray-200"
                    : ""
                }`}
              />
              {countryDropdownOpen && !isSouser && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                  {COUNTRY_OPTIONS.filter((opt) =>
                    t(`profile.${opt.key}`)
                      .toLowerCase()
                      .includes(countrySearch.toLowerCase()),
                  ).map((opt) => {
                    const isSelected = countryFilter.includes(opt.code);
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => {
                          setCountryFilter((prev) =>
                            prev.includes(opt.code)
                              ? prev.filter((c) => c !== opt.code)
                              : [...prev, opt.code],
                          );
                          setCountrySearch("");
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          isSelected
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${
                            isSelected
                              ? "bg-indigo-600 border-indigo-600 text-white"
                              : "border-gray-300"
                          }`}
                        >
                          {isSelected && "\u2713"}
                        </span>
                        {t(`profile.${opt.key}`)}
                      </button>
                    );
                  })}
                  {COUNTRY_OPTIONS.filter((opt) =>
                    t(`profile.${opt.key}`)
                      .toLowerCase()
                      .includes(countrySearch.toLowerCase()),
                  ).length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400 italic">
                      {t("common.no_results", "No results found")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sending Org */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("feed.form.sending_org", "Sending Org")}
            </label>
            <select
              value={sendingOrgFilter}
              onChange={(e) => setSendingOrgFilter(e.target.value)}
              disabled={isSouser || sendingOrgs.length === 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <option value="">
                {sendingOrgs.length === 0
                  ? t("feed.form.sending_org_select_country", "Select 1 country first")
                  : t("feed.form.sending_org_all", "All")}
              </option>
              {sendingOrgs.map((org) => (
                <option key={org.value} value={org.value}>
                  {org.label}
                </option>
              ))}
            </select>
          </div>

          {/* Created By */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("feed.management.created_by", "Created By")}
            </label>
            <input
              type="text"
              value={createdByFilter}
              onChange={(e) => setCreatedByFilter(e.target.value)}
              placeholder={t(
                "feed.management.filter_created_by",
                "Search author...",
              )}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Show Publicly */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("feed.management.show_publicly", "Show Publicly")}
            </label>
            <select
              value={showPubliclyFilter}
              onChange={(e) =>
                setShowPubliclyFilter(e.target.value as "all" | "public" | "private")
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">{t("inquiry.status.all")}</option>
              <option value="public">{t("feed.management.public", "Public")}</option>
              <option value="private">{t("feed.management.private", "Private")}</option>
            </select>
          </div>

          {/* Status (date-based active/inactive) */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t("feed.management.status", "Status")}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">{t("inquiry.status.all")}</option>
              <option value="active">{t("feed.management.active_only", "Active")}</option>
              <option value="inactive">{t("feed.management.inactive_only", "Inactive")}</option>
            </select>
          </div>
        </div>

        {/* Filter actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm md:text-xs py-1 text-gray-500 hover:text-gray-700"
          >
            {t("feed.management.clear_filters", "Clear filters")}
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {t("feed.management.result_count", {
                count: sorted.length,
                defaultValue: "{{count}} results",
              })}
            </span>
            <Button variant="secondary" size="sm" onClick={handleExportCSV}>
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {t("feed.management.export_csv", "Export CSV")}
            </Button>
          </div>
        </div>
      </div>

      {/* Success / Error banners */}
      {deleteSuccess && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {deleteSuccess}
          <button
            type="button"
            onClick={() => setDeleteSuccess("")}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            &times;
          </button>
        </div>
      )}
      {deleteError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {deleteError}
          <button
            type="button"
            onClick={() => setDeleteError("")}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            &times;
          </button>
        </div>
      )}

      {/* Results table */}
      {loading ? (
        <Spinner />
      ) : sorted.length === 0 ? (
        <EmptyState title={t("home.no_announcements")} />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs text-gray-500">
              {t("settings.search_showing", {
                from: page * 50 + 1,
                to: Math.min((page + 1) * 50, sorted.length),
                total: sorted.length,
              })}
            </p>
          </div>
          <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left sticky top-0 z-10">
                <tr>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("title")}
                  >
                    {t("feed.form.title")}
                    <SortIcon columnKey="title" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("company")}
                  >
                    {t("feed.form.companies")}
                    <SortIcon columnKey="company" />
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-600">
                    {t("feed.form.country", "Country")}
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-600">
                    {t("feed.form.sending_org", "Sending Org")}
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("created_by")}
                  >
                    {t("feed.management.created_by", "Created By")}
                    <SortIcon columnKey="created_by" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("date")}
                  >
                    {t("feed.management.date", "Date")}
                    <SortIcon columnKey="date" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-indigo-600 transition-colors text-center"
                    onClick={() => handleSort("comments")}
                  >
                    {t("feed.management.comments_col", "Comments")}
                    <SortIcon columnKey="comments" />
                  </th>
                  <th
                    className="px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-indigo-600 transition-colors text-center"
                    onClick={() => handleSort("views")}
                  >
                    {t("feed.management.views_col", "Views")}
                    <SortIcon columnKey="views" />
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-center">
                    {t("feed.poll.results")}
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-600">
                    {t("feed.management.show_publicly", "Show Publicly")}
                  </th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-right">
                    {t("feed.management.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedAnnouncements.map((a) => (
                  <tr
                    key={a.row_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => { if (canEdit(a)) navigate(`/announcements/edit/${a.row_id}`); }}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 truncate max-w-[200px]">
                        {a.title}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(a.target_companies || a.company || [])
                          .slice(0, 2)
                          .map((c: any, i: number) => {
                            const label =
                              typeof c === "string"
                                ? c
                                : c?.label || c?.company_name || "";
                            return (
                              <span key={i} className="text-xs text-gray-500">
                                {label}
                                {i <
                                Math.min(
                                  (a.target_companies || a.company || [])
                                    .length,
                                  2,
                                ) -
                                  1
                                  ? ","
                                  : ""}
                              </span>
                            );
                          })}
                        {(a.target_companies || a.company || []).length > 2 && (
                          <span className="text-xs text-gray-400">
                            +
                            {(a.target_companies || a.company || []).length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {(a.country || []).length > 0
                        ? (a.country || []).join(", ")
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {a.sending_org || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {a.created_by_name || a.author_name || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {a.created_at ? formatDate(a.created_at) : "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setViewingCommentsId(a.row_id); }}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                          />
                        </svg>
                        {a.comments_count ?? 0}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleViewClick(a.row_id); }}
                        disabled={loadingViewersId === a.row_id}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors disabled:opacity-50"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        {loadingViewersId === a.row_id
                          ? "..."
                          : (a.view_count ?? 0)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {a.poll ? <button type="button" onClick={(e) => { e.stopPropagation(); if (canEdit(a)) setViewingPoll(a); }} disabled={!canEdit(a)} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 disabled:cursor-default">
                        <span aria-hidden="true">{a.poll.is_locked ? "🔒" : "◉"}</span>
                        {a.poll.total_respondents !== undefined && <span>{a.poll.total_respondents}</span>}
                      </button> : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={a.active ? "success" : "default"}>
                        {a.active
                          ? t("feed.management.public", "Public")
                          : t("feed.management.private", "Private")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit(a) && <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); setDeletingId(a.row_id); }}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </Button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                {t("settings.page_prev")}
              </Button>
              <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                {t("settings.page_next")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Viewers modal */}
      <ViewersModal
        isOpen={showViewers}
        onClose={() => setShowViewers(false)}
        viewers={viewers}
      />

      {viewingPoll && <PollResultsModal
        isOpen
        rowId={viewingPoll.row_id}
        canWrite={canEdit(viewingPoll)}
        onClose={() => setViewingPoll(null)}
        onLockChange={(locked) => setAnnouncements((prev) => prev.map((item) => item.row_id === viewingPoll.row_id && item.poll ? { ...item, poll: { ...item.poll, is_locked: locked, is_open: locked ? false : item.poll.is_open } } : item))}
      />}

      {/* Comments modal */}
      <Modal
        isOpen={viewingCommentsId !== null}
        onClose={() => setViewingCommentsId(null)}
        title={t("feed.management.comments_col", "Comments")}
      >
        {viewingCommentsId !== null && (
          <CommentSection
            announcementId={viewingCommentsId}
            currentUserId={userData?.id ?? ""}
            currentUserName={formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
            isOfficer={canManage}
            readOnly
          />
        )}
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deletingId !== null}
        onClose={() => {
          if (!deleteLoading) setDeletingId(null);
        }}
        title={t("feed.management.delete_title", "Delete Announcement")}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t(
              "feed.management.delete_confirm",
              "Are you sure you want to delete this announcement? This action cannot be undone.",
            )}
          </p>
          {deletingId && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {announcements.find((a) => a.row_id === deletingId)?.title}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={deleteLoading}
              onClick={() => setDeletingId(null)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={deleteLoading}
              onClick={handleDeleteConfirm}
            >
              {t("common.delete", "Delete")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
